// Видимость разделов сайта — карта режимов из админки (backend: services/nav_visibility.py).
//
// Режим на пункт бокового меню: 'all' — виден всем, 'staff' — только сотрудникам,
// 'off' — скрыт у всех (страница остаётся доступна сотруднику по прямой ссылке,
// чтобы владелец мог проверить выключенный раздел).
//
// Карта грузится один раз на сессию вкладки: меню рисуется на каждой странице,
// а настройка меняется редко. Ошибка сети не должна прятать разделы — тогда
// работают DEFAULTS, то есть поведение из кода.
import api from './client'

/** link — «по ссылке» (08.10): пункта нет в меню ни у кого, страницы раздела открыты всем по прямой ссылке
 *  (разделы-посадочные: ссылки из поиска, рекламы и плиток главной). Возможностям такой режим не даётся. */
export type NavMode = 'all' | 'staff' | 'off' | 'link'

/** Поведение из кода: то же, что `default` в backend/app/services/nav_visibility.py. */
export const NAV_DEFAULTS: Record<string, NavMode> = {
	scene: 'staff',
	// Не пункт меню, а возможность внутри генератора: «@» в промпте, готовые персонажи, «Локация» и «Предмет»
	// в меню фото-слота (backend: nav_visibility.FEATURE_ITEMS). Свои персонажи работают при любом режиме.
	scene_tools: 'staff',
	// Возможность внутри генератора: кнопка «Повторить движение» в виджете (23.10, строка под «Видео» в «Меню сайта»).
	motion_tool: 'staff',
	flows: 'staff',
	trends: 'all',
	smart_assistants: 'staff',
	assistants: 'all',
	images: 'all',
	video: 'all',
	audio: 'all',
	tools: 'all',
	covers: 'all',
	slides: 'all',
	product_card: 'all',
}

/** Адреса разделов — для стража маршрутов (components/NavRouteGuard.tsx).
 *  Совпадают с `path` в backend/app/services/nav_visibility.py. */
export const NAV_PATHS: { id: string; path: string }[] = [
	{ id: 'scene', path: '/scene' },
	{ id: 'flows', path: '/flows' },
	{ id: 'trends', path: '/bots/trends' },
	{ id: 'smart_assistants', path: '/assistants' },
	{ id: 'assistants', path: '/bots/assistants' },
	{ id: 'images', path: '/bots/images' },
	{ id: 'video', path: '/bots/video' },
	{ id: 'audio', path: '/bots/audio' },
	{ id: 'covers', path: '/bots/tools/covers' },
	{ id: 'slides', path: '/bots/tools/slides' },
	{ id: 'product_card', path: '/bots/tools/product-card' },
]

export type NavModes = Record<string, NavMode>
/** Ярлыки «new» у пунктов меню (владелец 15.10: вешаются в админке «Меню сайта», не в коде): {id: 'new'}. */
export type NavBadges = Record<string, 'new'>
/** Поведение из кода, пока карта не загрузилась: то же, что badge_default в backend nav_visibility.NAV_ITEMS. */
export const NAV_BADGE_DEFAULTS: NavBadges = { scene: 'new', smart_assistants: 'new' }

/** Раскладка меню (владелец 15.10: перетаскивание в «Меню сайта»): [{id, parent}] в порядке следования, parent — id
 *  раздела-группы, глубина одна. Адреса, режимы и ярлыки от раскладки не зависят. */
export interface NavLayoutRow {
	id: string
	parent: string | null
}
export type NavLayout = NavLayoutRow[]
/** Порядок из кода — то же, что NAV_ITEMS + LAYOUT_PARENT_DEFAULTS в backend nav_visibility.py. */
export const NAV_LAYOUT_DEFAULTS: NavLayout = [
	{ id: 'scene', parent: null }, { id: 'trends', parent: null }, { id: 'smart_assistants', parent: null },
	{ id: 'assistants', parent: null }, { id: 'images', parent: null }, { id: 'video', parent: null },
	{ id: 'audio', parent: null }, { id: 'flows', parent: null }, { id: 'tools', parent: null },
	{ id: 'product_card', parent: 'tools' }, { id: 'covers', parent: 'tools' }, { id: 'slides', parent: 'tools' },
]
let cache: NavModes | null = null
let badgeCache: NavBadges | null = null
let layoutCache: NavLayout | null = null
let inflight: Promise<NavModes> | null = null

function isMode(v: unknown): v is NavMode {
	return v === 'all' || v === 'staff' || v === 'off' || v === 'link'
}

/** Режим пункта: настройка админа → значение из кода → «виден всем». */
export function navMode(modes: NavModes | null, id: string): NavMode {
	const fromAdmin = modes?.[id]
	if (isMode(fromAdmin)) return fromAdmin
	return NAV_DEFAULTS[id] ?? 'all'
}

/** Видно ли пункт/страницу этому пользователю. Сотрудник видит всё, включая 'off'. */
export function navVisible(mode: NavMode, staff: boolean): boolean {
	return mode === 'all' || mode === 'link' || staff
}

/** Возможность внутри страницы (не раздел): 'all' — всем, 'staff' — сотрудникам, 'off' — никому. */
export function featureOpen(mode: NavMode, staff: boolean): boolean {
	return mode === 'all' || (mode === 'staff' && staff)
}

/** Пункт в МЕНЮ: 'off' и 'link' скрыты даже у сотрудника — в первом случае раздел выключен (ссылка остаётся
 *  сотруднику), во втором раздел живёт только по ссылке (страницы открыты всем). */
export function navVisibleInMenu(mode: NavMode, staff: boolean): boolean {
	if (mode === 'off' || mode === 'link') return false
	return mode === 'all' || staff
}

export function cachedNavModes(): NavModes | null {
	return cache
}

export function cachedNavBadges(): NavBadges | null {
	return badgeCache
}

export function cachedNavLayout(): NavLayout | null {
	return layoutCache
}

/** Есть ли ярлык «new» у пункта: карта админки (в ней уже учтены дефолты сервера), пока её нет — дефолты из кода. */
export function navBadge(badges: NavBadges | null, id: string): boolean {
	return (badges ?? NAV_BADGE_DEFAULTS)[id] === 'new'
}

export async function fetchNavModes(): Promise<NavModes> {
	if (cache) return cache
	if (!inflight) {
		inflight = api
			.request<{ ok: boolean; items?: NavModes; badges?: Record<string, unknown>; layout?: unknown }>('/api/v1/nav')
			.then((res) => {
				const items = res?.items && typeof res.items === 'object' ? res.items : {}
				const clean: NavModes = {}
				for (const [id, mode] of Object.entries(items)) if (isMode(mode)) clean[id] = mode
				cache = clean
				// Старый сервер без badges — остаёмся на дефолтах из кода (null), новый — его карта, даже пустая.
				if (res?.badges && typeof res.badges === 'object') {
					const badges: NavBadges = {}
					for (const [id, v] of Object.entries(res.badges)) if (v === 'new') badges[id] = 'new'
					badgeCache = badges
				}
				// Раскладка: старый сервер без layout — порядок из кода (null).
				if (Array.isArray(res?.layout)) {
					layoutCache = res.layout
						.filter((r): r is { id: string; parent?: unknown } => !!r && typeof r === 'object' && typeof (r as { id?: unknown }).id === 'string')
						.map((r) => ({ id: r.id, parent: typeof r.parent === 'string' && r.parent ? r.parent : null }))
				}
				return clean
			})
			.catch(() => ({}) as NavModes)
			.finally(() => {
				inflight = null
			})
	}
	return inflight
}
